import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Device, deviceStore } from '@/stores/deviceStore';
import { formatTime } from '@/utils/formatters';
import { useToast } from '@/hooks/use-toast';

interface DeviceControlModalProps {
  device: Device;
  isOpen: boolean;
  onClose: () => void;
  isMapView?: boolean; // New prop to handle map view specific styling
}

export const DeviceControlModal = observer(({ device, isOpen, onClose, isMapView = false }: DeviceControlModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const status = deviceStore.deviceStatus[device.device_id] || {};
  const s = status.s || {};
  const sc = status.sc || {};
  const relay = device.relay;
  
  // Get force state
  const isForced = relay && s[`r${relay}f` as keyof typeof s] === 1;
  const [forceState, setForceState] = useState(isForced || false);
  
  // Schedule times
  const defaultOnTime = relay && sc[`r${relay}o` as keyof typeof sc] ? 
    sc[`r${relay}o` as keyof typeof sc]?.toString().includes(':') ?
      sc[`r${relay}o` as keyof typeof sc] as string :
      parseCronToTime(sc[`r${relay}o` as keyof typeof sc] as string) :
    '20:30';
    
  const defaultOffTime = relay && sc[`r${relay}c` as keyof typeof sc] ?
    sc[`r${relay}c` as keyof typeof sc]?.toString().includes(':') ?
      sc[`r${relay}c` as keyof typeof sc] as string :
      parseCronToTime(sc[`r${relay}c` as keyof typeof sc] as string) :
    '06:30';
  
  const [onTime, setOnTime] = useState(defaultOnTime);
  const [offTime, setOffTime] = useState(defaultOffTime);
  const [loading, setLoading] = useState({
    schedule: false,
    force: false,
    solarUpdate: false
  });
  const [message, setMessage] = useState<{text: string, type: 'success' | 'error'} | null>(null);
  
  // Parse cron to HH:MM
  function parseCronToTime(cron: string): string {
    if (!cron || !cron.includes(' ')) return '00:00';
    
    const parts = cron.split(' ');
    if (parts.length >= 2) {
      return `${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`;
    }
    
    return '00:00';
  }
  
  // Debug state for action execution
  const [actionExecuting, setActionExecuting] = useState<string | null>(null);
  
  // Helper to close with delay after successful action
  const closeWithDelay = (delayMs = 1000) => {
    console.log('Scheduling modal close with delay', delayMs);
    setTimeout(() => {
      console.log('Closing modal after successful action');
      onClose();
    }, delayMs);
  };
  
  // Handle force toggle
  const handleForceToggle = async (checked: boolean) => {
    if (!relay) return;
    
    setActionExecuting('force toggle');
    setLoading(prev => ({ ...prev, force: true }));
    try {
      console.log('Starting force toggle API call');
      await deviceStore.forceRelayState(device.device_id, relay, checked);
      console.log('Force toggle API call completed');
      setForceState(checked);
      
      toast({
        title: t('deviceControl.notifications.deviceUpdated'),
        description: checked 
          ? t('deviceControl.notifications.forceStateEnabled') 
          : t('deviceControl.notifications.forceStateDisabled'),
      });
      
      // Close modal after success
      closeWithDelay();
    } catch (error: unknown) {
      console.error('Error toggling force state:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      toast({
        title: t('deviceControl.notifications.updateFailed'),
        description: errorMessage || t('deviceControl.notifications.failedToToggleForce'),
        variant: "destructive"
      });
      // Revert UI state
      setForceState(!checked);
    } finally {
      setLoading(prev => ({ ...prev, force: false }));
      setActionExecuting(null);
    }
  };
  
  // Handle schedule update
  const handleScheduleUpdate = async () => {
    if (!relay) return;
    
    setActionExecuting('schedule update');
    setLoading(prev => ({ ...prev, schedule: true }));
    setMessage(null);
    
    try {
      console.log('Starting schedule update API call');
      await deviceStore.updateSchedule(device.device_id, relay, onTime, offTime);
      console.log('Schedule update API call completed');
      
      setMessage({
        text: t('deviceControl.notifications.scheduleUpdated'),
        type: 'success'
      });
      
      toast({
        title: t('deviceControl.notifications.scheduleUpdated'),
        description: t('deviceControl.notifications.scheduleUpdateSuccess', { onTime, offTime }),
      });
      
      // Close modal after success
      closeWithDelay();
    } catch (error: unknown) {
      console.error('Error updating schedule:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      setMessage({
        text: errorMessage || t('deviceControl.notifications.failedToUpdateSchedule'),
        type: 'error'
      });
      
      toast({
        title: t('deviceControl.notifications.updateFailed'),
        description: errorMessage || t('deviceControl.notifications.failedToUpdateSchedule'),
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, schedule: false }));
      setActionExecuting(null);
    }
  };
  
  // Handle solar update
  const handleSolarUpdate = async () => {
    setActionExecuting('solar update');
    setLoading(prev => ({ ...prev, solarUpdate: true }));
    
    try {
      console.log('Starting solar update API call');
      await deviceStore.sendCommand(device.device_id, 'solar_update');
      console.log('Solar update API call completed');
      
      toast({
        title: t('deviceControl.solarUpdate', 'Solar Update'),
        description: t('deviceControl.notifications.solarUpdateSuccess', 'Solar update command sent successfully'),
      });
      
      // Close modal after success
      closeWithDelay();
    } catch (error: unknown) {
      console.error('Error sending solar update command:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      toast({
        title: t('deviceControl.solarUpdate', 'Solar Update'),
        description: errorMessage || t('deviceControl.notifications.solarUpdateFailed', 'Failed to send solar update command'),
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, solarUpdate: false }));
      setActionExecuting(null);
    }
  };
  
  // Check if any action is loading
  const isAnyActionLoading = Object.values(loading).some(Boolean) || !!actionExecuting;
  
  // Safe close - only if no action is executing
  const safeClose = () => {
    if (!isAnyActionLoading) {
      onClose();
      console.log('Modal closing normally');
    } else {
      console.log('Closing prevented due to active action:', actionExecuting);
    }
  };
  
  // Effect for ESC key handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!isAnyActionLoading) {
          console.log('Closing modal with Escape key');
          onClose();
        } else {
          console.log('Escape key prevented - action executing:', actionExecuting);
          e.preventDefault();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isAnyActionLoading, actionExecuting]);
  
  if (!isOpen) return null;
  
  // Special rendering for map view to handle z-index issues
  if (isMapView) {
    return (
      <div 
        className="fixed inset-0" 
        style={{ 
          zIndex: 9999999,
          touchAction: 'none' // Prevent touch events from bubbling
        }}
      >
        {/* Backdrop - high z-index but transparent in the center */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={safeClose}
          onKeyDown={(e) => e.key === 'Escape' && safeClose()}
          tabIndex={0}
          role="button"
          aria-label="Close modal"
          style={{ zIndex: 10000000 }}
        />
        
        {/* Content div with extremely high z-index */}
        <div 
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
          style={{ 
            zIndex: 10000001,
            pointerEvents: 'auto'
          }}
        >
          {/* Debug banner */}
          {actionExecuting && (
            <div className="absolute top-0 left-0 right-0 bg-yellow-500 text-black text-center text-xs py-1">
              Action executing: {actionExecuting}
            </div>
          )}
          
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t('deviceControl.title')}: {device.device_id}</h3>
            <button 
              type="button"
              onClick={safeClose}
              disabled={isAnyActionLoading}
              className={`text-gray-500 hover:text-gray-700 text-2xl font-bold ${isAnyActionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label="Close"
              style={{ zIndex: 10000002 }}
            >
              ×
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">{t('deviceControl.deviceDetails.deviceId')}:</span> {device.device_id}</div>
              <div><span className="font-medium">{t('deviceControl.deviceDetails.area')}:</span> {device.area || '-'}</div>
              <div><span className="font-medium">{t('deviceControl.deviceDetails.point')}:</span> {device.point || '-'}</div>
              <div><span className="font-medium">{t('deviceControl.deviceDetails.relay')}:</span> {relay || '-'}</div>
            </div>
            
            {relay && (
              <>
                <div className="flex items-center justify-between">
                  <span>{t('deviceControl.forceState')}:</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={forceState} 
                      onChange={(e) => handleForceToggle(e.target.checked)}
                      disabled={loading.force}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                  </label>
                </div>
                
                <div className="border-t pt-4 mt-2">
                  <div className="font-medium mb-3">{t('deviceControl.schedule')}:</div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="space-y-2">
                      <label htmlFor="onTime">{t('deviceControl.onTime')}:</label>
                      <input 
                        id="onTime" 
                        type="time" 
                        value={onTime} 
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setOnTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="offTime">{t('deviceControl.offTime')}:</label>
                      <input 
                        id="offTime" 
                        type="time" 
                        value={offTime} 
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setOffTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  
                  <button 
                    type="button"
                    onClick={handleScheduleUpdate} 
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading.schedule}
                  >
                    {loading.schedule ? t('deviceControl.updating') : t('deviceControl.updateSchedule')}
                  </button>
                  
                  {message && (
                    <div className={`text-sm mt-2 ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {message.text}
                    </div>
                  )}
                </div>
              </>
            )}
            
            <div className="border-t pt-4">
              <button 
                type="button"
                onClick={handleSolarUpdate}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading.solarUpdate}
              >
                {loading.solarUpdate ? t('deviceControl.updating') : t('deviceControl.solarUpdate', 'Solar Update')}
              </button>
            </div>
            
            {isAnyActionLoading && (
              <div className="text-center text-sm text-blue-600 font-medium">
                {t('common.loading', 'Loading...')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Standard modal for non-map views
  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop div - separate from content to handle clicks properly */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={safeClose}
        onKeyDown={(e) => e.key === 'Escape' && safeClose()}
        tabIndex={0}
        role="button"
        aria-label="Close modal"
      />
      
      {/* Content div - stops propagation of clicks */}
      <div 
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-[60]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Debug info */}
        {actionExecuting && (
          <div className="absolute top-0 left-0 right-0 bg-yellow-500 text-black text-center text-xs py-1">
            Action executing: {actionExecuting}
          </div>
        )}
        
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{t('deviceControl.title')}: {device.device_id}</h3>
          <button 
            type="button"
            onClick={safeClose}
            disabled={isAnyActionLoading}
            className={`text-gray-500 hover:text-gray-700 text-2xl font-bold z-[100] ${isAnyActionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">{t('deviceControl.deviceDetails.deviceId')}:</span> {device.device_id}</div>
            <div><span className="font-medium">{t('deviceControl.deviceDetails.area')}:</span> {device.area || '-'}</div>
            <div><span className="font-medium">{t('deviceControl.deviceDetails.point')}:</span> {device.point || '-'}</div>
            <div><span className="font-medium">{t('deviceControl.deviceDetails.relay')}:</span> {relay || '-'}</div>
          </div>
          
          {relay && (
            <>
              <div className="flex items-center justify-between">
                <span>{t('deviceControl.forceState')}:</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={forceState} 
                    onChange={(e) => handleForceToggle(e.target.checked)}
                    disabled={loading.force}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>
              
              <div className="border-t pt-4 mt-2">
                <div className="font-medium mb-3">{t('deviceControl.schedule')}:</div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-2">
                    <label htmlFor="onTime">{t('deviceControl.onTime')}:</label>
                    <input 
                      id="onTime" 
                      type="time" 
                      value={onTime} 
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setOnTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="offTime">{t('deviceControl.offTime')}:</label>
                    <input 
                      id="offTime" 
                      type="time" 
                      value={offTime} 
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setOffTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                
                <button 
                  type="button"
                  onClick={handleScheduleUpdate} 
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading.schedule}
                >
                  {loading.schedule ? t('deviceControl.updating') : t('deviceControl.updateSchedule')}
                </button>
                
                {message && (
                  <div className={`text-sm mt-2 ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {message.text}
                  </div>
                )}
              </div>
            </>
          )}
          
          <div className="border-t pt-4">
            <button 
              type="button"
              onClick={handleSolarUpdate}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading.solarUpdate}
            >
              {loading.solarUpdate ? t('deviceControl.updating') : t('deviceControl.solarUpdate', 'Solar Update')}
            </button>
          </div>
          
          {isAnyActionLoading && (
            <div className="text-center text-sm text-blue-600 font-medium">
              {t('common.loading', 'Loading...')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}); 